import { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  OPEN: { bg: 'rgba(75, 157, 255, 0.1)', color: '#4B9DFF' },
  UNDER_REVIEW: { bg: 'rgba(255, 193, 7, 0.1)', color: '#FFC107' },
  RESOLVED: { bg: 'rgba(71, 255, 176, 0.1)', color: '#47FFB0' },
  REJECTED: { bg: 'rgba(255, 107, 107, 0.1)', color: '#FF6B6B' },
};

export default function BrokerDisputesPage({ user }) {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDisputes();
  }, []);

  const fetchDisputes = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/broker/disputes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDisputes(res.data.disputes || []);
    } catch (err) {
      toast.error('Failed to load disputes');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '32px', color: theme.colors.text.muted }}>Loading...</div>
    );
  }

  return (
    <div data-testid="broker-disputes-page">
      <div style={{ marginBottom: '32px' }}>
        <h1
          style={{
            fontFamily: theme.fonts.heading,
            fontSize: '28px',
            fontWeight: '700',
            color: theme.colors.text.primary,
            marginBottom: '8px',
          }}
        >
          Disputes
        </h1>
        <p style={{ color: theme.colors.text.secondary, fontSize: '15px' }}>
          Disputes related to your accepted portfolios. {disputes.length > 0 && `(${disputes.length})`}
        </p>
      </div>

      {disputes.length === 0 ? (
        <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
          <CardContent className="py-16 text-center">
            <CheckCircle size={48} style={{ color: '#47FFB0', margin: '0 auto 16px' }} />
            <p
              style={{
                fontFamily: theme.fonts.heading,
                fontSize: '18px',
                fontWeight: '600',
                color: theme.colors.text.primary,
                marginBottom: '8px',
              }}
            >
              No Disputes
            </p>
            <p style={{ color: theme.colors.text.muted, fontSize: '14px', maxWidth: '400px', margin: '0 auto' }}>
              No disputes have been filed against your accepted portfolios. Keep up the good work!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {disputes.map((d) => {
            const style = STATUS_STYLES[d.status] || STATUS_STYLES.OPEN;
            return (
              <Card
                key={d.id}
                data-testid={`broker-dispute-${d.id}`}
                style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}
              >
                <CardContent className="pt-5 pb-5">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <p
                        style={{
                          fontFamily: theme.fonts.heading,
                          fontSize: '16px',
                          fontWeight: '600',
                          color: theme.colors.text.primary,
                          marginBottom: '4px',
                        }}
                      >
                        {d.subject}
                      </p>
                      <p style={{ fontSize: '13px', color: theme.colors.text.muted }}>
                        {d.dispute_type} &middot; {d.portfolio_name || 'No portfolio'} &middot; By: {d.buyer_email}
                      </p>
                    </div>
                    <span
                      style={{
                        fontSize: '11px',
                        padding: '4px 10px',
                        borderRadius: '999px',
                        background: style.bg,
                        color: style.color,
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {d.status}
                    </span>
                  </div>
                  <p style={{ fontSize: '14px', color: theme.colors.text.secondary, lineHeight: '1.5', marginBottom: '8px' }}>
                    {d.description}
                  </p>
                  {d.admin_notes && (
                    <p
                      style={{
                        fontSize: '13px',
                        padding: '8px 12px',
                        borderRadius: theme.radius.md,
                        background: 'rgba(255, 193, 7, 0.05)',
                        color: '#FFC107',
                        border: '1px solid rgba(255, 193, 7, 0.2)',
                      }}
                    >
                      Admin notes: {d.admin_notes}
                    </p>
                  )}
                  <p style={{ fontSize: '12px', color: theme.colors.text.muted, marginTop: '8px' }}>
                    Filed: {d.created_at ? new Date(d.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
